'use client';

/**
 * Performance Panel
 *
 * Educates users about AI response performance, network diagnostics,
 * and helps reduce false "slow" complaints.
 */

import { Activity, AlertCircle, Brain, Network, Zap } from 'lucide-react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('web:component:performance-panel');
import React, { useState } from 'react';
import { formatHealthReport, runHealthChecks } from '../services/health-check-service';
import { toast } from '../utils/toast';
import { Button } from './button';

export const PerformancePanel: React.FC = () => {
  const [isRunningDiagnostics, setIsRunningDiagnostics] = useState(false);
  const [diagnosticsResult, setDiagnosticsResult] = useState<string | null>(null);

  const handleRunDiagnostics = async () => {
    setIsRunningDiagnostics(true);
    toast.info('Running Diagnostics', 'Testing connectivity to all services...');

    try {
      const report = await runHealthChecks();
      const formatted = formatHealthReport(report);
      setDiagnosticsResult(formatted);

      if (report.overallStatus === 'healthy') {
        toast.success('All Systems Operational', 'Your connection to all services is healthy.');
      } else if (report.overallStatus === 'degraded') {
        toast.warning('Slow Connection Detected', 'Some services are responding slowly.');
      } else {
        toast.error('Connection Issues', 'Unable to reach some services.');
      }
    } catch (error) {
      logger.error('Failed to run diagnostics:', error);
      toast.error('Diagnostics Failed', 'Could not complete network diagnostics.');
    } finally {
      setIsRunningDiagnostics(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2">Understanding Performance</h2>
        <p className="text-sm text-muted-foreground">
          Learn why AI responses take time and how to diagnose real issues
        </p>
      </div>

      {/* AI Response Speed Explanation */}
      <div className="glass-surface rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-500/10">
            <Brain className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">
              The "Animation" is NOT an Animation!
            </h3>
            <p className="text-xs text-muted-foreground">Most common misconception</p>
          </div>
        </div>

        <div className="space-y-3 text-sm text-foreground">
          <p>
            When you see text appearing <strong>word-by-word</strong>, you're watching the AI{' '}
            <strong>generate its response in real-time</strong>. This is{' '}
            <span className="text-blue-500 font-semibold">NOT a slow animation</span> — it's the
            actual thinking process!
          </p>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Response Speed Depends On:
            </p>
            <ul className="space-y-1.5 text-xs">
              <li className="flex items-start gap-2">
                <Zap className="w-3.5 h-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Question Complexity:</strong> Simple questions = 5-10 seconds, complex
                  medical analysis = 30-60 seconds
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="w-3.5 h-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Response Length:</strong> Longer answers take more time to generate
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="w-3.5 h-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>AI Model Load:</strong> When many users ask questions simultaneously,
                  responses slow down
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="w-3.5 h-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Your Network:</strong> Slow internet adds 1-5 seconds of latency
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Common Misunderstandings */}
      <div className="glass-surface rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-500/10">
            <AlertCircle className="w-5 h-5 text-orange-500" />
          </div>
          <h3 className="text-base font-semibold text-foreground">Common Misconceptions</h3>
        </div>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <span className="text-lg">❌</span>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">"The animation is too slow"</p>
              <p className="text-xs text-muted-foreground">
                It's not an animation! You're seeing the AI think in real-time. This is normal
                behavior.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="text-lg">❌</span>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">"The app is frozen/stuck"</p>
              <p className="text-xs text-muted-foreground">
                Look for the typing indicator (blinking cursor). If it's there, the AI is working.
                Complex questions can take 30-60 seconds.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="text-lg">✅</span>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">Normal Behavior</p>
              <p className="text-xs text-muted-foreground">
                AI responses typically take 10-30 seconds for medical questions. Very complex
                queries can take up to 60 seconds. This is expected!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Network Diagnostics */}
      <div className="glass-surface rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-500/10">
            <Network className="w-5 h-5 text-green-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-foreground">Network Diagnostics</h3>
            <p className="text-xs text-muted-foreground">Test your connection to our services</p>
          </div>
        </div>

        <p className="text-sm text-foreground">
          If you're experiencing genuine slowness, run this test to check if it's a network issue:
        </p>

        <Button
          onClick={handleRunDiagnostics}
          disabled={isRunningDiagnostics}
          variant="secondary"
          size="md"
          className="w-full"
        >
          {isRunningDiagnostics ? (
            <>
              <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
              Running Diagnostics...
            </>
          ) : (
            <>
              <Activity className="w-4 h-4" />
              Run Network Diagnostics
            </>
          )}
        </Button>

        {diagnosticsResult && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">Diagnostics Results:</p>
            <pre className="p-4 rounded-xl bg-muted/50 text-xs font-mono text-foreground overflow-x-auto whitespace-pre-wrap max-h-96 overflow-y-auto">
              {diagnosticsResult}
            </pre>
          </div>
        )}
      </div>

      {/* Tips for Faster Responses */}
      <div className="glass-surface rounded-2xl p-5 space-y-4">
        <h3 className="text-base font-semibold text-foreground">💡 Tips for Faster Responses</h3>

        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-green-500 font-bold">✓</span>
            <span>
              <strong>Ask focused questions</strong> — Shorter questions get faster responses
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-500 font-bold">✓</span>
            <span>
              <strong>Limit context</strong> — Don't include unnecessary details or very long text
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-500 font-bold">✓</span>
            <span>
              <strong>Check your network</strong> — Ensure stable internet connection
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-500 font-bold">✓</span>
            <span>
              <strong>Be patient</strong> — Medical AI requires careful analysis, which takes time
            </span>
          </li>
        </ul>
      </div>

      {/* When to Report a Real Issue */}
      <div className="glass-surface rounded-2xl p-5 space-y-4 border-2 border-red-500/20">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-500/10">
            <AlertCircle className="w-5 h-5 text-red-500" />
          </div>
          <h3 className="text-base font-semibold text-foreground">When to Report an Issue</h3>
        </div>

        <p className="text-sm text-foreground">
          Report a performance bug <strong>ONLY</strong> if you experience:
        </p>

        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-red-500">•</span>
            <span>Responses taking longer than 2 minutes consistently</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">•</span>
            <span>App becoming completely unresponsive (no cursor blinking)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">•</span>
            <span>Network diagnostics showing all services "down" or "blocked"</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">•</span>
            <span>App crashes or freezes requiring restart</span>
          </li>
        </ul>

        <div className="pt-3 border-t border-border/30">
          <p className="text-xs text-muted-foreground">
            <strong>Before reporting:</strong> Run Network Diagnostics above. If all services show
            "healthy" but responses are slow, it's likely the AI thinking, not a bug!
          </p>
        </div>
      </div>
    </div>
  );
};

export default PerformancePanel;
